const shortFormatRegex = /^([1-2]\d{3})-(0?[1-9]|1[0-2])-(0?[1-9]|[1-2]\d|3[0-1])$/;

/**
 * Date utilities, encapsulated so they can later be localized.
 */
const dateUtils = {
  isDateObject: o => Object.prototype.toString.call(o) === '[object Date]',

  /**
   * Parse a string into a Date object, Apply the local timezone if it's missing
   *
   * @param {Date|String|int} string - a date, date string, or time in milliseconds
   * @returns {Date}
   */
  dateFromString: (string) => {
    if (dateUtils.isDateObject(string)) {
      return string; // Nothing to do here
    }

    if (typeof string === 'string') {
      const matchData = string.match(shortFormatRegex);
      // If the string matches YYYY-MM-DD, apply the timezone
      if (matchData) {
        return new Date(parseInt(matchData[1]), parseInt(matchData[2]) - 1, parseInt(matchData[3]));
      }
    }

    return new Date(string);
  },

  /**
   * Format a date in the form, "April 2018."
   *
   * @param {Date|String|int} date - A date, in any format compatible with the Date object.
   * @returns {string}
   */
  formatMonthYear: date => {
    // Force into a date object, regardless of the original type.
    date = dateUtils.dateFromString(date);
    return MONTHS[date.getMonth()] + ' ' + date.getFullYear();
  },

  /**
   * Format a date in the form, "1 April 2018."
   *
   * @param {Date|String|int} date - A date, in any format compatible with the Date object.
   * @returns {string}
   */
  formatDayMonthYear: date => {
    date = dateUtils.dateFromString(date);
    return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
  },

  /**
   * Format a date in the form, "1 Apr 2018"
   *
   * @param {Date|String|int} date - A date, in any format compatible with the Date object.
   * @returns {string}
   */
  formatDateMonthShortYear: date => {
    date = dateUtils.dateFromString(date);
    return `${date.getDate()} ${MONTHS_SHORT[date.getMonth()]} ${date.getFullYear()}`;
  },

  /**
   * Format a date in ISO-8601 standard form:
   * 2019-01-23T00:00:00.000Z
   * @param {Date|String|int} date - A date, in any format compatible with the Date object.
   * @returns {string}
   */
  formatISO: date => dateUtils.dateFromString(date).toISOString(),

  /**
   * Return true if the string is a date of the form YYYY-MM-DD
   *
   * @param {String} s - A potential date.
   * @returns {boolean}
   */
  isShortFormat: s => shortFormatRegex.test(s),

  /**
   * Format a date in YYYY-MM-DD form
   *
   * @param {Date|String|int} date - A date, in any format compatible with the Date object.
   * @returns {string}
   */
  formatShort: date => {
    date = dateUtils.dateFromString(date);
    if (!date.getTime()) {
      return '';
    }

    const day = pad2(date.getDate());
    const month = pad2(date.getMonth() + 1);

    return `${date.getFullYear()}-${month}-${day}`;
  },

  /**
   * Copy the time component of a Date object to another, or to a string of the form YYYY-MM-DD.
   *
   * @param {Date|String} dst - A Date object, or a string of the form YYYY-MM-DD.
   * @param {Date} src - Copy the time component from this.
   * @returns {Date}
   */
  copyTime: (dst, src) => {
    src = src || new Date();

    if (typeof dst === 'string') {
      // If there's no timezone, the Date constructor assumes UTC, which is almost always wrong.
      // Since there's no way to override this behavior, we have to deconstruct the string.
      if (dateUtils.isShortFormat(dst)) {
        let d = new Date(src);
        const parts = dst.split(/\D+/).map(p => parseInt(p, 10));
        d.setFullYear(parts[0]);
        d.setMonth(parts[1] - 1);
        d.setDate(parts[2]);
        return d;
      }

      // Unknown format. Do our best.
      dst = new Date(dst);
    }

    let d = new Date(dst);
    if (src) {
      d.setHours(src.getHours());
      d.setMinutes(src.getMinutes());
      d.setSeconds(src.getSeconds());
    }
    return d;
  }
};
export default dateUtils;

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

export const MONTHS_SHORT = MONTHS.map(m => m.substring(0, 3));

const pad2 = i => ('0' + i).slice(-2);
